import { useState } from "react";
import { Job } from "@/components/jobs/types";
import { Button } from "@/components/ui/button";
import { UserSearch, Loader2, BrainCircuit } from "lucide-react";
import { toast } from "sonner";
import { useA2AProtocol } from "@/hooks/useA2AProtocol";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CandidateSourceActionProps {
  job: Job;
}

export const CandidateSourceAction = ({ job }: CandidateSourceActionProps) => {
  const [isSourceRunning, setIsSourceRunning] = useState(false);
  const { runMultiAgentTask } = useA2AProtocol();
  
  const handleSourceCandidates = async () => {
    setIsSourceRunning(true);
    
    try {
      // Prepare the job description for sourcing
      const sourcingPrompt = `Find candidates for the following job:
        Title: ${job.title}
        Company: ${job.company}
        Skills needed: ${job.skills?.join(', ')}
        Experience required: ${job.experience_required} years
        Location: ${job.location}
        Job type: ${job.type}
      `;
      
      // Always use the orchestration layer for candidate sourcing
      const response = await runMultiAgentTask(
        sourcingPrompt, 
        ['candidate-identification', 'resume-analysis', 'recruiting'],
        'complex'
      );
      
      // Log the sourcing request to create an audit trail
      try {
        await supabase.from('agent_audit_logs').insert({
          agent_id: 'executive-agent',
          action: 'CANDIDATE_SOURCING_ORCHESTRATION',
          request_content: sourcingPrompt,
          // Ensure response_content is always a string
          response_content: typeof response === 'string' ? response : JSON.stringify(response),
          tokens_used: sourcingPrompt.length,
          // Typecast metadata as any to match expected Json type for Postgrest
          metadata: {
            job_id: job.id,
            job_title: job.title,
            orchestration: true
          } as any
        });
      } catch (error) {
        console.error('Error logging sourcing activity:', error);
      }
      
      toast.success("Candidate sourcing has started", {
        description: "BrigAI is coordinating multiple specialized agents to find qualified candidates. Check the AI Hub for results."
      });
      
      // In a real implementation, we would wait for the complete results
      setTimeout(() => {
        setIsSourceRunning(false);
      }, 3000);
      
    } catch (error) {
      console.error("Error sourcing candidates:", error);
      toast.error("Failed to start candidate sourcing");
      setIsSourceRunning(false);
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center">
          <BrainCircuit className="h-5 w-5 mr-2 text-primary/70" />
          BrigAI Candidate Sourcing
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert className="bg-primary/5 border-primary/10">
            <AlertDescription className="text-sm">
              BrigAI will orchestrate specialized agents to find qualified candidates from connected job boards and professional networks.
            </AlertDescription>
          </Alert>
          
          <Button 
            onClick={handleSourceCandidates} 
            disabled={isSourceRunning}
            className="w-full"
          >
            {isSourceRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sourcing Candidates...
              </>
            ) : (
              <>
                <UserSearch className="h-4 w-4 mr-2" />
                Source Candidates
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
